import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { HandArrowDown, Hourglass } from "@phosphor-icons/react";
import { fmtDate } from "../lib/date";

const BLOCKED = ["out_of_service", "disposed", "written_off"];

// On-asset custody: shows a pending hand-over, or lets the holder/authorised user offer it to someone.
export const CustodyPanel = ({ asset, onChange }) => {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [recips, setRecips] = useState([]);
  const [toId, setToId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/asset-transfers/asset/${asset.id}`).then((r) => setInfo(r.data)).catch(() => setInfo(null)), [asset.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (open) { setToId(""); setNote(""); api.get("/asset-transfers/recipients").then((r) => setRecips(r.data.filter((p) => p.id !== asset.assigned_person_id))).catch(() => {}); }
  }, [open, asset.assigned_person_id]);

  const submit = async () => {
    if (busy) return; setBusy(true);
    try {
      await api.post("/asset-transfers/offer", { asset_id: asset.id, to_profile_id: toId, note: note || undefined });
      toast.success("Offer sent — custody changes only when they accept");
      setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  if (!info) return null;
  if (info.pending) return (
    <div data-testid="custody-pending" className="flex items-center gap-2 bg-amber-50 border-2 border-amber-300 rounded px-3 py-2 text-sm">
      <Hourglass size={18} weight="bold" className="text-amber-600 shrink-0" />
      <span className="font-semibold text-slate-800">{info.pending.awaiting_label}</span>
      <span className="text-xs text-slate-500 ml-auto">Offered by {info.pending.from_name} · {fmtDate(info.pending.created_at)}</span>
    </div>
  );
  if (!info.can_offer) return null;
  if (BLOCKED.includes(asset.status?.code)) return (
    <Button data-testid="custody-offer-blocked" disabled variant="outline" className="h-11 font-semibold border-2 self-start" title="Return it to service first">
      <HandArrowDown size={18} weight="bold" className="mr-1" />Offer custody — unavailable while {asset.status?.name}
    </Button>
  );
  return (
    <>
      <Button data-testid="custody-offer-button" onClick={() => setOpen(true)} variant="outline" className="h-11 font-semibold border-2 self-start">
        <HandArrowDown size={18} weight="bold" className="mr-1" />Offer custody to someone
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="custody-offer-dialog">
          <DialogHeader><DialogTitle className="font-display">Offer {asset.asset_number} to…</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">The asset stays with the current holder until the recipient accepts.</p>
          <select data-testid="custody-offer-recipient" className="h-11 border-2 border-slate-300 rounded px-2" value={toId} onChange={(e) => setToId(e.target.value)}>
            <option value="">Select person…</option>
            {recips.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <textarea data-testid="custody-offer-note" rows={2} className="border-2 border-slate-300 rounded p-2 text-sm" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <Button data-testid="custody-offer-submit" disabled={!toId || busy} onClick={submit} className="bg-indigo-600 hover:bg-indigo-700 font-semibold">Send offer</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
